import instantsearch from 'instantsearch.js';
import {
  connectPagination,
  connectSearchBox,
} from 'instantsearch.js/es/connectors';
import historyRouter from 'instantsearch.js/es/lib/routers/history';
import {
  configure,
  hits,
  pagination,
  stats,
} from 'instantsearch.js/es/widgets';

import { searchClient } from '../../state/SearchClient';

const { ALGOLIA_SEARCH_INDEX_NAME } = window as any;

const instantSearchRouter = historyRouter();

const search = searchClient
  ? instantsearch({
      searchClient,
      routing: false,
      initialUiState: {
        [ALGOLIA_SEARCH_INDEX_NAME]: {
          query: new URL(window.location.href).searchParams.get('q') || '',
        },
      },
      indexName: ALGOLIA_SEARCH_INDEX_NAME,
      onStateChange({ uiState, setUiState }) {
        // empty query string -> hide the search results & abort the search
        const results = document.querySelector('.search-results');
        const { query } = uiState[ALGOLIA_SEARCH_INDEX_NAME];

        if (query === '') {
          if (results) {
            results.classList.add('hidden');
          }
        } else if (results) {
          results.classList.remove('hidden');
        }

        setUiState(uiState);
      },
    })
  : null;

export const buildInstantSearch = ({ hasHits, emptyResultsMessage }) => {
  const virtualSearchBox = connectSearchBox(() => {});

  search.addWidgets([
    configure({
      attributesToSnippet: ['Summary:25', 'objectForTemplate:25'],
      snippetEllipsisText: '…',
      distinct: true,
      hitsPerPage: 10,
    }),
    virtualSearchBox({}),
  ]);

  const results = document.querySelector('.search-results');

  if (hasHits) {
    search.addWidgets([
      hits({
        container: '#hits',
        templates: {
          empty(results, { html }) {
            if (!emptyResultsMessage) {
              if (!results.query) {
                return '';
              }

              return html`<div>
                <h2 class="h3">
                  We didn’t find any results for ${results.query}
                </h2>
                <p>
                  Try searching for another term or contact us using the links
                  below.
                </p>
              </div>`;
            }
            return emptyResultsMessage;
          },
          item(hit, { html, components }) {
            // if rendering a hit, make sure `search-results` is not hidden
            if (results) {
              results.classList.remove('hidden');
            }
            return html`
              <a href="${hit.objectLink}" class="hit__link">
                <span class="h4 hit__title">
                  ${components.Highlight({ attribute: 'objectTitle', hit })}
                </span>
                <span class="hit__breadcrumb">${hit.objectLink}</span>
                <span
                  class="${hit.Summary
                    ? 'hit__summary'
                    : 'hit__missing-attribute'}"
                >
                  ${components.Snippet({ attribute: 'Summary', hit })}
                </span>
                <span
                  class="${hit.objectForTemplate
                    ? 'hit__snippet'
                    : 'hit__missing-attribute'}"
                >
                  ${components.Snippet({
                    attribute: 'objectForTemplate',
                    hit,
                  })}
                </span>
              </a>
            `;
          },
        },
      }),
      pagination({
        container: '#pagination',
        cssClasses: {
          root: 'pagination',
          link: 'pagination__button',
          list: 'pagination__list',
          previousPageItem: 'pagination__prev',
          nextPageItem: 'pagination__next',
        },
      }),
      stats({
        container: '#stats',
      }),
    ]);
  } else {
    // no hits, just virtual
    const virtualPagination = connectPagination(() => null);

    search.addWidgets([virtualPagination({})]);
  }

  return search;
};

export function setInstantSearchUiState(indexUiState) {
  if (!search) {
    return;
  }

  if (!search.started) {
    search.start();
  }

  search.setUiState((uiState) => ({
    ...uiState,
    [ALGOLIA_SEARCH_INDEX_NAME]: {
      ...uiState[ALGOLIA_SEARCH_INDEX_NAME],
      page: 1,
      ...indexUiState,
    },
  }));
}

export function debounce(fn, delay) {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

export const debouncedSetInstantSearchUiState = debounce(
  setInstantSearchUiState,
  800,
);

export function getInstantSearchUrl(indexUiState) {
  if (!search.started) {
    search.start();
  }

  return search.createURL({ [ALGOLIA_SEARCH_INDEX_NAME]: indexUiState });
}

// Return the InstantSearch index UI state.
export function getInstantSearchUiState() {
  const uiState = instantSearchRouter.read();

  return (uiState && uiState[ALGOLIA_SEARCH_INDEX_NAME]) || {};
}
